import { ui } from '@utils/i18n/uiCopy'
import conCosturaImg from '@assets/atemporal/Con costura.png'
import sinCosturasImg from '@assets/atemporal/Sin costuras.png'
import altura42Img from '@assets/atemporal/Altura 42.jpg'
import altura47Img from '@assets/atemporal/Altura 47.jpg'
import cojinRectangularImg from '@assets/atemporal/Rectangular.jpg'
import cojinMultiImg from '@assets/atemporal/multi.jpg'

export type BudgetIllustrationPack = {
  cushion: string
  height: string
  stitch: string
}

async function moduleUrlToDataUrl(url: string): Promise<string> {
  if (!url) return ''
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '')
      }
      reader.onerror = () => reject(new Error('read'))
      reader.readAsDataURL(blob)
    })
  } catch {
    return ''
  }
}

/** URL del bundler para previsualización en React (no data URL). */
export function getCushionIllustrationSrc(sceneCushionLabel: string): string {
  const l = String(sceneCushionLabel || '').toLowerCase()
  if (!l) return ''
  if (l.includes('rectangular')) return cojinRectangularImg
  if (l.includes('multicojin') || l.includes('cojin')) return cojinMultiImg
  return ''
}

export function getSeatHeightIllustrationSrc(seatHeightCm: number | null): string {
  if (seatHeightCm === 42) return altura42Img
  if (seatHeightCm === 47) return altura47Img
  return ''
}

export function getSeatStitchIllustrationSrc(seatStitchType: 'sin' | 'con'): string {
  return seatStitchType === 'sin' ? sinCosturasImg : conCosturaImg
}

/**
 * Data URLs para imprimir / iframe `srcdoc` (imágenes incrustadas).
 */
export async function fetchBudgetIllustrationsAsDataUrls(args: {
  sceneCushionLabel: string
  seatHeightCm: number | null
  seatStitchType: 'sin' | 'con'
}): Promise<BudgetIllustrationPack> {
  const cushionSrc = getCushionIllustrationSrc(args.sceneCushionLabel)
  const heightSrc = getSeatHeightIllustrationSrc(args.seatHeightCm)
  const stitchSrc =
    args.seatHeightCm !== null ? getSeatStitchIllustrationSrc(args.seatStitchType) : ''

  const [cushion, height, stitch] = await Promise.all([
    moduleUrlToDataUrl(cushionSrc),
    moduleUrlToDataUrl(heightSrc),
    moduleUrlToDataUrl(stitchSrc),
  ])
  return { cushion, height, stitch }
}

export function buildBudgetIllustrationsBlockHtml(
  escapeHtml: (s: string) => string,
  ctx: {
    sceneCushionLabel: string
    seatHeightCm: number | null
    seatCountLabel: string
    seatStitchLabel: string
    illus: BudgetIllustrationPack
  },
): string {
  const { sceneCushionLabel, seatHeightCm, seatCountLabel, seatStitchLabel, illus } = ctx
  const cards: string[] = []

  if (illus.cushion && sceneCushionLabel) {
    cards.push(`<div class="budget-opt-card">
      <div class="budget-opt-img-wrap">
        <img src="${illus.cushion}" alt="${escapeHtml(sceneCushionLabel)}" />
      </div>
      <div class="budget-opt-caption">${escapeHtml(sceneCushionLabel)}</div>
    </div>`)
  }

  if (illus.height && seatHeightCm !== null) {
    cards.push(`<div class="budget-opt-card">
      <div class="budget-opt-img-wrap">
        <img src="${illus.height}" alt="${escapeHtml(ui('cushionHeight'))} ${seatHeightCm} cm" />
      </div>
      <div class="budget-opt-caption">${escapeHtml(ui('cushionHeight'))}: ${seatHeightCm} cm</div>
    </div>`)
  }

  if (illus.stitch && seatHeightCm !== null) {
    cards.push(`<div class="budget-opt-card">
      <div class="budget-opt-img-wrap">
        <img src="${illus.stitch}" alt="${escapeHtml(seatStitchLabel)}" />
      </div>
      <div class="budget-opt-caption">${escapeHtml(seatCountLabel)} ${escapeHtml(seatStitchLabel)}</div>
    </div>`)
  }

  if (cards.length === 0) return ''

  return `<div class="budget-characteristics-block">
    <div class="line"><span class="label-strong">${escapeHtml(ui('options'))}:</span></div>
    <div class="budget-options-wrap">
      <div class="budget-options-row">${cards.join('')}</div>
    </div>
  </div>`
}
